
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { formatCertificateNumber } from '@/utils/certificateUtils';
import { 
  AlertCircle, 
  CheckCircle2, 
  Loader2,
  AlertTriangle,
  Filter
} from 'lucide-react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

export function VerificationChecklist() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedState, setSelectedState] = useState<string>('FL');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState<number>(
    Math.floor((new Date().getMonth() / 3)) + 1
  );
  const [isVerifyDialogOpen, setIsVerifyDialogOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<any>(null);
  const [verificationNotes, setVerificationNotes] = useState('');

  // Query for unverified certificates
  const { data: unverifiedCertificates, isLoading } = useQuery({
    queryKey: ['unverifiedCertificates', selectedState, selectedYear, selectedQuarter],
    queryFn: async () => {
      // Calculate quarter date range
      const startMonth = (selectedQuarter - 1) * 3;
      const startDate = new Date(selectedYear, startMonth, 1);
      const endDate = new Date(selectedYear, startMonth + 3, 0);
      
      // Format for PostgreSQL
      const formattedStartDate = startDate.toISOString();
      const formattedEndDate = endDate.toISOString();
      
      // Get certificates used in this period that are not verified
      const { data: unusedCerts, error: unusedError } = await supabase
        .from('certificate_number_inventory')
        .select(`
          id, 
          certificate_number, 
          state, 
          course_type, 
          is_used, 
          assigned_at,
          verification_status,
          reporting_status,
          certificates:used_by (id, unique_code)
        `)
        .eq('state', selectedState)
        .eq('verification_status', 'unverified')
        .gte('assigned_at', formattedStartDate)
        .lte('assigned_at', formattedEndDate);
      
      if (unusedError) throw unusedError;
      
      return unusedCerts || [];
    }
  });
  
  // Mutation to update verification status
  const verificationMutation = useMutation({
    mutationFn: async ({
      certificateId, 
      status, 
      notes
    }: {
      certificateId: string, 
      status: string, 
      notes: string
    }) => {
      // Update the certificate_number_inventory record
      const { error } = await supabase
        .from('certificate_number_inventory')
        .update({
          verification_status: status,
          verification_date: new Date().toISOString(),
          verified_by: (await supabase.auth.getUser()).data?.user?.id
        })
        .eq('id', certificateId);
        
      if (error) throw error;
      
      // Create a record in verification history
      const { data: certData } = await supabase
        .from('certificate_number_inventory')
        .select('certificate_number, report_id')
        .eq('id', certificateId)
        .single();
        
      if (certData) {
        await supabase
          .from('certificate_verification_history')
          .insert({
            certificate_number: certData.certificate_number,
            inventory_id: certificateId,
            report_id: certData.report_id,
            verification_status: status,
            verified_by: (await supabase.auth.getUser()).data?.user?.id,
            notes: notes
          });
      }
      
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unverifiedCertificates'] });
      toast({
        title: "Certificate Verified",
        description: "The certificate has been successfully verified.",
      });
      setIsVerifyDialogOpen(false);
      setSelectedCertificate(null);
      setVerificationNotes('');
    },
    onError: (error) => {
      toast({
        title: "Verification Failed",
        description: `Error: ${error.message}`,
        variant: "destructive"
      });
    }
  });
  
  const handleVerify = (certificate: any) => {
    setSelectedCertificate(certificate);
    setIsVerifyDialogOpen(true);
  };
  
  const confirmVerification = () => {
    if (!selectedCertificate) return;
    
    verificationMutation.mutate({
      certificateId: selectedCertificate.id,
      status: 'verified',
      notes: verificationNotes
    });
  };
  
  const yearOptions = Array.from({ length: 5 }, (_, i) => 
    new Date().getFullYear() - 2 + i
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Certificate Verification</CardTitle>
        <CardDescription>
          Verify certificates for quarterly reporting compliance
        </CardDescription>
        <div className="flex flex-wrap gap-2 mt-4">
          <Select
            value={selectedState}
            onValueChange={setSelectedState}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="FL">Florida</SelectItem>
              <SelectItem value="CA">California</SelectItem>
              <SelectItem value="TX">Texas</SelectItem>
              <SelectItem value="NY">New York</SelectItem>
            </SelectContent>
          </Select>
          
          <Select
            value={selectedYear.toString()}
            onValueChange={(value) => setSelectedYear(parseInt(value))}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select
            value={selectedQuarter.toString()}
            onValueChange={(value) => setSelectedQuarter(parseInt(value))}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue placeholder="Quarter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Q1</SelectItem>
              <SelectItem value="2">Q2</SelectItem>
              <SelectItem value="3">Q3</SelectItem>
              <SelectItem value="4">Q4</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Certificate Number</TableHead>
                <TableHead>Course Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Used Date</TableHead>
                <TableHead>Reporting Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {unverifiedCertificates && unverifiedCertificates.length > 0 ? (
                unverifiedCertificates.map((cert) => (
                  <TableRow key={cert.id}>
                    <TableCell className="font-medium">
                      {formatCertificateNumber(cert.certificate_number)}
                    </TableCell>
                    <TableCell>{cert.course_type}</TableCell>
                    <TableCell>
                      {cert.is_used ? (
                        <Badge variant="outline" className="bg-yellow-50">Used</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-green-50">Available</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {cert.assigned_at 
                        ? new Date(cert.assigned_at).toLocaleDateString() 
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {cert.reporting_status === 'reported' ? (
                        <Badge variant="outline" className="bg-green-50 text-green-800 flex gap-1 items-center">
                          <CheckCircle2 className="h-3 w-3" /> Reported
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-800 flex gap-1 items-center">
                          <AlertTriangle className="h-3 w-3" /> Not Reported
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => handleVerify(cert)}>
                        Verify
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4">
                    <div className="flex flex-col items-center gap-2">
                      <CheckCircle2 className="h-8 w-8 text-green-500" />
                      <p>All certificates in this period have been verified.</p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <CardFooter className="border-t px-6 py-4 bg-muted/50">
        <div className="text-sm text-muted-foreground">
          Verify all certificates to ensure compliance with state reporting requirements.
          Unverified certificates may cause discrepancies in quarterly reports.
        </div>
      </CardFooter>
      
      {/* Verification Dialog */}
      <Dialog open={isVerifyDialogOpen} onOpenChange={setIsVerifyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verify Certificate</DialogTitle>
            <DialogDescription>
              Confirm that you have reviewed this certificate and it is ready to be marked as verified.
            </DialogDescription>
          </DialogHeader>
          
          {selectedCertificate && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium mb-1">Certificate Number</p>
                  <p className="text-sm">{formatCertificateNumber(selectedCertificate.certificate_number)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium mb-1">Course Type</p>
                  <p className="text-sm">{selectedCertificate.course_type}</p>
                </div>
                <div>
                  <p className="text-sm font-medium mb-1">Status</p>
                  <p className="text-sm">{selectedCertificate.is_used ? "Used" : "Available"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium mb-1">State</p>
                  <p className="text-sm">{selectedCertificate.state}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm font-medium mb-1">Verification Notes</p>
                <Textarea
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder="Add any notes about this verification"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsVerifyDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmVerification} 
              disabled={verificationMutation.isPending}
            >
              {verificationMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirm Verification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
